import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import { HelmetProvider } from "react-helmet-async";
import { AuthProvider, useAuth } from "./context/AuthContext";
import "./App.css";

import Navbar from "./components/Navbar";
import Footer from "./components/Footer";

import Home from "./pages/Home";
import Feed from "./pages/Feed";
import CampaignDetail from "./pages/CampaignDetail";
import CreateCampaign from "./pages/CreateCampaign";
import Login from "./pages/Login";
import Register from "./pages/Register";
import UserDashboard from "./pages/UserDashboard";
import AdminDashboard from "./pages/AdminDashboard";
import About from "./pages/About";
import Team from "./pages/Team";
import DirectorDetail from "./pages/DirectorDetail";
import Contact from "./pages/Contact";
import DonationSuccess from "./pages/DonationSuccess";
import AuthCallback from "./pages/AuthCallback";
import ForgotPassword from "./pages/ForgotPassword";
import ResetPassword from "./pages/ResetPassword";
import NotFound from "./pages/NotFound";

function ProtectedRoute({ children, role }) {
  const { user, loading } = useAuth();
  const location = useLocation();
  if (loading) return <div className="min-h-screen flex items-center justify-center"><div className="text-brand-muted">Loading…</div></div>;
  if (!user) return <Navigate to="/login" state={{ from: location }} replace />;
  if (role && user.role !== role && !(role === "any-creator" && (user.role === "requester" || user.role === "admin"))) {
    return <Navigate to="/" replace />;
  }
  return children;
}

function Shell() {
  const location = useLocation();

  // CRITICAL: When returning from Emergent OAuth, the session_id arrives in the URL
  // fragment. Process it FIRST (synchronously, during render) so ProtectedRoute can't
  // bounce the user to /login before the session cookie is set.
  if (location.hash?.includes("session_id=")) {
    return (
      <div className="App font-sans-ui bg-brand-bg min-h-screen flex flex-col">
        <main className="flex-1">
          <AuthCallback />
        </main>
      </div>
    );
  }

  return (
    <div className="App font-sans-ui bg-brand-bg min-h-screen flex flex-col">
      <Navbar />
      <main className="flex-1">
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/campaigns" element={<Feed />} />
          <Route path="/campaigns/:id" element={<CampaignDetail />} />
          <Route path="/create" element={<ProtectedRoute role="any-creator"><CreateCampaign /></ProtectedRoute>} />
          <Route path="/login" element={<Login />} />
          <Route path="/register" element={<Register />} />
          <Route path="/dashboard" element={<ProtectedRoute><UserDashboard /></ProtectedRoute>} />
          <Route path="/admin" element={<ProtectedRoute role="admin"><AdminDashboard /></ProtectedRoute>} />
          <Route path="/about" element={<About />} />
          <Route path="/team" element={<Team />} />
          <Route path="/team/:slug" element={<DirectorDetail />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/news" element={<Feed />} />
          <Route path="/donation-success" element={<DonationSuccess />} />
          <Route path="/auth/callback" element={<AuthCallback />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="*" element={<NotFound />} />
        </Routes>
      </main>
      <Footer />
    </div>
  );
}

export default function App() {
  return (
    <HelmetProvider>
      <AuthProvider>
        <BrowserRouter>
          <Toaster position="top-center" richColors theme="light" />
          <Shell />
        </BrowserRouter>
      </AuthProvider>
    </HelmetProvider>
  );
}
